/**
 * Copyright © 2026 Adnuntius AS.
 */
import {Component, ElementRef, Input, NgZone, OnChanges, OnDestroy, OnInit, SimpleChanges, ViewChild} from '@angular/core';
import * as Highcharts from 'highcharts';

import template from './co2-stats.component.html';
import {StatsResource} from '../../../components/api/resources/resource/stats-resources/stats-resource';
import {OBJECT_TYPE} from '../constants/object-type';
import {AdnLogService} from '../../../components/util/adn-log.service';

@Component({
  standalone: false,
  selector: 'adn-co2-stats-component',
  template
})
export class Co2StatsComponent implements OnInit, OnChanges, OnDestroy {
  @Input() model: any;
  @Input() objectType: string;

  @ViewChild('chartContainer')
  set chartContainer(chartContainer: ElementRef<HTMLElement>) {
    this.chartContainerRef = chartContainer;
    this.renderChart();
  }

  readonly engaged = true;

  allStats: any = {};
  haveStats = false;
  error = false;
  isPending = false;
  noData: string | null = null;
  rImpsTotals = 0;
  trans = '';

  private canceller: any;
  private chart: Highcharts.Chart | null = null;
  private chartContainerRef: ElementRef<HTMLElement> | null = null;
  private chartOptions: Highcharts.Options | null = null;
  private lastRequestKey = '';

  constructor(
    private readonly statsResource: StatsResource,
    private readonly ngZone: NgZone,
    private readonly adnLog: AdnLogService
  ) {
  }

  get resolvedObjectType(): string {
    return this.objectType;
  }

  ngOnInit(): void {
    this.ensureStatsLoaded();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.model || changes.objectType) {
      this.ensureStatsLoaded();
    }
  }

  ngOnDestroy(): void {
    if (this.canceller) {
      this.canceller.resolve('Destroyed component');
    }
    this.destroyChart();
  }

  private ensureStatsLoaded(): void {
    if (!this.model?.id) {
      return;
    }

    const requestKey = `${this.resolvedObjectType || 'Creative'}:${this.model.id}`;
    if (requestKey === this.lastRequestKey) {
      return;
    }

    this.lastRequestKey = requestKey;
    this.loadStats();
  }

  private loadStats(): void {
    const objectTypeEl = OBJECT_TYPE[this.resolvedObjectType] || OBJECT_TYPE.Creative;
    const co2Params: Record<string, any> = {
      groupBy: 'MONTHLY'
    };

    co2Params[objectTypeEl.apiParam + 'Id'] = this.model.id;

    this.trans = objectTypeEl.name.toLowerCase();
    this.error = false;
    this.haveStats = false;
    this.isPending = false;
    this.noData = null;
    this.rImpsTotals = 0;
    this.chartOptions = null;
    this.destroyChart();
    this.allStats = {
      programmaticIndustry: {
        name: 'Industry Average (Programmatic)',
        ratio: 4.24
      },
      directIndustry: {
        name: 'Industry Average (Direct)',
        ratio: 0.66
      },
      adn: {}
    };

    this.statsResource.getCo2(co2Params, (stats) => {
      this.ngZone.run(() => {
        const rendered = Number(stats?.totals?.rendered) || 0;
        const totalEmissions = Number(stats?.totals?.totalEmissions) || 0;
        const emissions = Math.trunc(totalEmissions * 1000);
        const ratio = rendered > 0 ? emissions / rendered : 0;
        const series: Highcharts.SeriesOptionsType[] = [];

        this.rImpsTotals = rendered;

        Object.keys(this.allStats).forEach((key) => {
          if (key === 'adn') {
            return;
          }
          const benchmark = this.allStats[key];
          const benchmarkEmission = Math.trunc(benchmark.ratio * this.rImpsTotals);
          benchmark.emissions = benchmarkEmission;
          benchmark.adnRatio = benchmarkEmission > 0 ? emissions / benchmarkEmission : 0;
          series.push({
            type: 'bar',
            name: benchmark.name,
            data: [benchmark.emissions]
          });
        });

        this.allStats.adn = {
          emissions,
          emissionsRaw: emissions,
          ratio
        };

        series.push({
          type: 'bar',
          name: 'This ' + objectTypeEl.name,
          data: [emissions]
        });

        this.chartOptions = this.createChartOptions(series);
        this.haveStats = true;
        this.renderChart();
      });
    }, (data) => {
      this.ngZone.run(() => {
        this.adnLog.warn(data);
        if (data.status !== -1) {
          this.error = true;
        }
      });
    }, this);
  }

  private createChartOptions(series: Highcharts.SeriesOptionsType[]): Highcharts.Options {
    return {
      chart: {
        type: 'bar',
        height: 200
      },
      yAxis: {
        title: {
          text: 'Emissions in grammes'
        }
      },
      xAxis: {
        categories: [''],
        title: {
          text: null
        }
      },
      tooltip: {
        shared: true,
        valueSuffix: 'g'
      },
      series,
      title: {
        text: null
      },
      credits: {
        enabled: false
      },
      accessibility: {
        enabled: false
      },
      exporting: {
        enabled: true
      }
    };
  }

  private renderChart(): void {
    if (!this.chartContainerRef?.nativeElement || !this.chartOptions || this.rImpsTotals <= 0) {
      return;
    }

    this.destroyChart();
    this.chart = Highcharts.chart(this.chartContainerRef.nativeElement, this.chartOptions);
  }

  private destroyChart(): void {
    if (this.chart) {
      this.chart.destroy();
      this.chart = null;
    }
  }
}
